import { useState, useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import {
  api,
  type OrgWithStats,
  type MspDashboardStats,
  type MSP,
  type SessionInfo,
} from './api/client'
import { NavItem } from './components/StatusIndicator'
import LoginPage from './LoginPage'
import SetupPage from './SetupPage'
import OrgsPage from './pages/OrgsPage'
import OrgDetailPage from './pages/OrgDetailPage'
import DeviceDetailPage from './pages/DeviceDetailPage'
import SettingsPage from './pages/SettingsPage'
import AlertsPage from './pages/AlertsPage'

// Re-export for backward compatibility with any existing imports
export { NetworkQualityBadge } from './components/StatusIndicator'

// Placeholder for pages not yet built
function PlaceholderPage({ title, description }: { title: string, description: string }) {
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white tracking-tight">{title}</h1>
        <p className="text-slate-500 mt-1">{description}</p>
      </div>
    </div>
  )
}

// Main layout with sidebar
function DashboardLayout({ session, msp, dashboardStats, organizations, setOrganizations, setDashboardStats, onLogout }: {
  session: SessionInfo
  msp: MSP | null
  dashboardStats: MspDashboardStats
  organizations: OrgWithStats[]
  setOrganizations: React.Dispatch<React.SetStateAction<OrgWithStats[]>>
  setDashboardStats: React.Dispatch<React.SetStateAction<MspDashboardStats | null>>
  onLogout: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()

  const activePage = location.pathname.startsWith('/settings') ? 'settings'
    : location.pathname.startsWith('/analytics') ? 'analytics'
    : location.pathname.startsWith('/alerts') ? 'alerts'
    : 'clients'

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Subtle grid background */}
      <div
        className="fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      {/* Sidebar */}
      <aside className="w-64 bg-slate-900/80 backdrop-blur border-r border-slate-800 flex flex-col relative z-10">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <svg className="w-6 h-6 text-slate-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            </div>
            <div>
              <h1 className="font-bold text-white text-lg tracking-tight">Network Weather</h1>
              <p className="text-xs text-slate-500">MSP Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <div className="space-y-1">
            <NavItem icon="grid" label="Clients" active={activePage === 'clients'} onClick={() => navigate('/orgs')} />
            <NavItem icon="chart" label="Analytics" active={activePage === 'analytics'} onClick={() => navigate('/analytics')} />
            <NavItem icon="bell" label="Alerts" active={activePage === 'alerts'} onClick={() => navigate('/alerts')} badge={dashboardStats.critical > 0 ? dashboardStats.critical : undefined} />
            <NavItem icon="settings" label="Settings" active={activePage === 'settings'} onClick={() => navigate('/settings')} />
          </div>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/50">
            {session?.picture ? (
              <img
                src={session.picture}
                alt=""
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-sm font-medium text-white">
                {session?.name?.split(' ').map(w => w[0]).join('').slice(0, 2) || msp?.name.split(' ').map(w => w[0]).join('').slice(0, 2) || 'M'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{session?.name || msp?.name || 'MSP'}</p>
              <p className="text-xs text-slate-500 truncate">{session?.email || 'admin@msp.io'}</p>
            </div>
            <button
              onClick={() => navigate('/settings')}
              className="p-1.5 text-slate-500 hover:text-white hover:bg-slate-700 rounded transition-colors"
              title="Settings"
              aria-label="Open settings"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <button
              onClick={onLogout}
              className="p-1.5 text-slate-500 hover:text-white hover:bg-slate-700 rounded transition-colors"
              title="Sign out"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-8 relative z-10">
        <Routes>
          <Route path="/orgs" element={
            <OrgsPage
              organizations={organizations}
              setOrganizations={setOrganizations}
              dashboardStats={dashboardStats}
              setDashboardStats={setDashboardStats}
              msp={msp}
            />
          } />
          <Route path="/orgs/:orgId" element={<OrgDetailPage />} />
          <Route path="/orgs/:orgId/devices/:clientId" element={<DeviceDetailPage />} />
          <Route path="/settings" element={<SettingsPage msp={msp} />} />
          <Route path="/analytics" element={<PlaceholderPage title="Analytics" description="Fleet analytics and reporting are coming soon." />} />
          <Route path="/alerts" element={<AlertsPage organizations={organizations} />} />
          <Route path="*" element={<Navigate to="/orgs" replace />} />
        </Routes>
      </main>
    </div>
  )
}

export default function App() {
  const [session, setSession] = useState<SessionInfo | null>(null)
  const [authChecked, setAuthChecked] = useState(false)
  const [msp, setMsp] = useState<MSP | null>(null)
  const [organizations, setOrganizations] = useState<OrgWithStats[]>([])
  const [dashboardStats, setDashboardStats] = useState<MspDashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  // Check auth on mount
  useEffect(() => {
    api.getSession()
      .then(sessionInfo => {
        setSession(sessionInfo)
        setAuthChecked(true)
      })
      .catch(() => {
        setSession({ authenticated: false })
        setAuthChecked(true)
      })
  }, [])

  // Load dashboard data when authenticated. `loading` starts true and is
  // cleared when this fetch settles. Signed-out renders return the login
  // routes before `loading` is read, so there is nothing to clear here when
  // the session is not authenticated.
  useEffect(() => {
    if (!session?.authenticated) return

    // First get the current MSP, then load its data
    api.getCurrentMsp().then(mspRes => {
      const currentMsp = mspRes.data
      setMsp(currentMsp)

      return Promise.all([
        api.listOrganizations({ mspId: currentMsp.mspId }),
        api.getDashboardStats(currentMsp.mspId),
      ])
    }).then((results) => {
      if (!results) return
      const [orgsRes, statsRes] = results
      setOrganizations(orgsRes.data ?? [])
      setDashboardStats(statsRes.data ?? null)
    }).catch(err => {
      console.error('Failed to load dashboard data:', err)
      // If we get an auth error, log out
      if (err.message?.includes('Unauthorized')) {
        setSession({ authenticated: false })
      }
    }).finally(() => setLoading(false))
  }, [session?.authenticated])

  const handleLogout = async () => {
    await api.logout()
    setSession({ authenticated: false })
    setMsp(null)
    setOrganizations([])
    setDashboardStats(null)
  }

  // Show loading while checking auth
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-500">Checking authentication...</div>
      </div>
    )
  }

  // Show login page if not authenticated
  if (!session?.authenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  // Show setup page if MSP hasn't completed onboarding
  if (session?.setupComplete === false) {
    return (
      <Routes>
        <Route path="/setup" element={
          <SetupPage
            suggestedOrgName={session.suggestedOrgName}
            onComplete={(updatedMsp) => {
              setMsp(updatedMsp)
              setSession(prev => prev ? { ...prev, setupComplete: true } : prev)
            }}
          />
        } />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    )
  }

  if (loading || !dashboardStats) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    )
  }

  return (
    <DashboardLayout
      session={session}
      msp={msp}
      dashboardStats={dashboardStats}
      organizations={organizations}
      setOrganizations={setOrganizations}
      setDashboardStats={setDashboardStats}
      onLogout={handleLogout}
    />
  )
}
